import {
    FGButton,
    FGIcons,
    FGStack,
    FGStackItem,
    FGTile,
    FGTypography,
} from '@fg/design-system';
import React, { useState, useEffect, useCallback } from 'react';
import rootStore from '../../RootStore';

type Side = 'left' | 'right';
type Props = {
    defaultOpen?: boolean;
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
    side?: Side;
    width?: number;
    collapsedSize?: number;
    style?: React.CSSProperties;
    children: React.ReactNode;
    title: string;
};

const TRANSITION_TIME = 200;

export const PickerDrawer = ({
    defaultOpen,
    open,
    onOpenChange,
    side = 'left',
    width = 320,
    collapsedSize = 64,
    children,
    title,
    style,
}: Props) => {
    const isControlled = typeof open === 'boolean';
    const [internalOpen, setInternalOpen] = useState(defaultOpen);
    const resolvedOpen = isControlled ? open : internalOpen;
    useEffect(() => {
        if (isControlled) return;
        setInternalOpen(defaultOpen);
    }, [defaultOpen]);
    //todo: useCallback
    const setOpen = value => {
        if (!isControlled) {
            setInternalOpen(value);
        }
        onOpenChange?.(value);
        setTimeout(() => {
            rootStore.chartStore.updateDimensions();
        }, TRANSITION_TIME + 100);
    };
    //todo: useCallback
    const toggleOpen = () => setOpen(!resolvedOpen);
    return (
        <div
            style={{
                height: '100%',
                position: 'relative',
                width: resolvedOpen ? width : collapsedSize,
                transition: `width ${TRANSITION_TIME}ms ease`,
                display: 'flex',
                flexDirection: 'row',
                ...style,
            }}>
            <FGTile
                padding="default"
                appearance="primary"
                alignItems="flex-start"
                style={{
                    height: '100%',
                    borderRadius: 0,
                    overflow: 'visible',
                }}>
                <FGStack
                    direction="column"
                    style={{ height: '100%', width: '100%', position: 'relative' }}>
                    <FGStackItem>
                        <PickerDrawerHeader
                            title={title}
                            side={side}
                            open={resolvedOpen}
                            onToggle={toggleOpen}
                        />
                    </FGStackItem>
                    {resolvedOpen && (
                        <FGStackItem style={{ flex: 1 }}>
                            {children}
                        </FGStackItem>
                    )}
                </FGStack>
            </FGTile>
        </div>
    );
};

type PickerDrawerHeaderProps = {
    title: string;
    side: Side;
    open: boolean;
    onToggle: () => void;
};

const OPEN_ICON = 'Sidebar';
const CLOSE_ICON = 'Sidebar';
const PickerDrawerHeader = ({
    title,
    side,
    open,
    onToggle,
}: PickerDrawerHeaderProps) => {
    const renderToggleButton = useCallback(
        () => (
            <FGButton
                onClick={onToggle}
                variant="secondary"
                style={{ zIndex: 288 }}>
                <FGIcons icon={open ? OPEN_ICON : CLOSE_ICON} size={15} />
            </FGButton>
        ),
        [open, onToggle]
    );
    const renderTitle = useCallback(() => {
        return (
            <FGTypography
                tag="h4"
                variant="headline4"
                color="strong"
                cssStyle={{ whiteSpace: 'nowrap' }}>
                {title}
            </FGTypography>
        );
    }, [title]);

    if (side === 'left') {
        return (
            <FGStack
                alignItems="center"
                gap={12}
                justifyContent={open ? 'flex-start' : 'center'}>
                <FGStackItem>{renderToggleButton()}</FGStackItem>
                {open && <FGStackItem>{renderTitle()}</FGStackItem>}
            </FGStack>
        );
    }
    return (
        <FGStack
            alignItems="center"
            justifyContent={open ? 'space-between' : 'center'}>
            {open && <FGStackItem>{renderTitle()}</FGStackItem>}
            <FGStackItem>{renderToggleButton()}</FGStackItem>
        </FGStack>
    );
};
